import { useState, useRef, useEffect } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { TipContent } from "@/components/TipContent";
import { useSearch } from "@/hooks/useSearch";
import { PinButton } from "@/components/PinButton";
import { cn } from "@/lib/utils";

interface SearchBarProps {
  onSearchToggle?: (isOpen: boolean) => void;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearchToggle,
  autoFocus = false,
  placeholder = "Search tips...",
  className 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { query, searchResults, isSearching, search, clearSearch, hasResults } = useSearch();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onSearchToggle?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearchToggle]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    search(value);
    setIsOpen(value.length > 0);
    onSearchToggle?.(value.length > 0);
  };

  const handleClear = () => {
    setInputValue("");
    clearSearch();
    setIsOpen(false);
    onSearchToggle?.(false);
    inputRef.current?.focus();
  };

  const handleResultClick = () => {
    setIsOpen(false);
    onSearchToggle?.(false);
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-primary/20 text-primary rounded px-0.5">
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <div ref={containerRef} className={cn("relative w-full max-w-md", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          className="pl-10 pr-10 h-11 bg-background/50 backdrop-blur-sm border-border/50 focus:border-primary/50"
          onFocus={() => {
            if (inputValue.length > 0) {
              setIsOpen(true);
              onSearchToggle?.(true);
            }
          }}
        />
        
        {inputValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        
        {isSearching && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <Card className="absolute top-full left-0 right-0 mt-2 max-h-96 overflow-y-auto z-50 bg-background/95 backdrop-blur-md border-border/50 shadow-lg">
          <CardContent className="p-0">
            {isSearching && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
                <span className="text-muted-foreground">Searching...</span>
              </div>
            )}
            
            {!isSearching && query && !hasResults && (
              <div className="text-center py-8 px-4">
                <Search className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-2">No tips found</p>
                <p className="text-sm text-muted-foreground">
                  Try searching with different keywords
                </p>
              </div>
            )}
            
            {!isSearching && hasResults && (
              <div className="py-2">
                <div className="px-4 py-2 border-b border-border/20">
                  <p className="text-sm text-muted-foreground">
                    {searchResults.length} tip{searchResults.length !== 1 ? 's' : ''} found
                  </p>
                </div>
                
                {searchResults.slice(0, 5).map((tip) => (
                  <Link
                    key={tip.id}
                    to={`/tips/${tip.id}`}
                    onClick={handleResultClick}
                    className="block hover:bg-muted/50 transition-colors"
                  >
                    <div className="px-4 py-3 border-b border-border/10 last:border-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium line-clamp-1 mb-1">
                            {highlightMatch(tip.title, query)}
                          </h4>
                          <TipContent 
                            content={tip.content}
                            variant="preview"
                            className="text-sm line-clamp-2 mb-2"
                            highlightMatch={highlightMatch}
                            query={query}
                          />
                          <div className="flex items-center gap-2">
                            {tip.category && (
                              <Badge variant="secondary" className="text-xs">
                                {highlightMatch(tip.category, query)}
                              </Badge>
                            )}
                            {tip.rating && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <span>★ {tip.rating.toFixed(1)}</span>
                                {tip.rating_count && <span>({tip.rating_count})</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <PinButton 
                            tipId={tip.id}
                            size="sm"
                            variant="ghost"
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
                
                {searchResults.length > 5 && (
                  <div className="px-4 py-3 border-t border-border/20">
                    <Link
                      to={`/search?q=${encodeURIComponent(query)}`}
                      onClick={handleResultClick}
                      className="text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      View all {searchResults.length} results →
                    </Link>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};